import React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {CurrencyType} from 'types/Currency'

import {Radio} from '@vio/atlas/Radio'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {splitPopularAndOtherCurrencies} from '@vio/core/src/localization/business/currency'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {SearchInput} from '../../SearchInput'
import {SelectorList} from '../SelectorList/SelectorList'

const PriceDisplayTitle = styled(Text)`
  margin-bottom: ${cssTheme.layout.spacing.s300};
`

const PriceDisplayWrapper = styled.div`
  padding: 0 ${cssTheme.layout.spacing.s200};
  margin-bottom: ${cssTheme.layout.spacing.s600};
`

const RadioGroup = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  ${linariaMq.desktopXs} {
    flex-direction: row;
    align-items: center;
    gap: ${cssTheme.layout.spacing.s800};
  }
`

interface Props {
  isMobile?: boolean
  value: string
  onChange: (code: string) => void
  currencies: CurrencyType[]
  onSearchInputFocus?: () => void
  showPriceDisplaySetting?: boolean
  showTotalPrices?: boolean
  onChangePriceDisplay?: (showTotal: boolean) => void
  includeTaxes?: boolean
}

const splitPopularAndOther = (items: CurrencyType[]) => {
  const {currenciesList, popularCurrenciesList} =
    splitPopularAndOtherCurrencies(items)

  return {
    itemsList: currenciesList.filter(Boolean),
    popularItemsList: popularCurrenciesList.filter(Boolean)
  }
}

export const messages = defineMessages({
  nightlyPrice: {
    id: 'nightlyPrice',
    defaultMessage: 'Nightly price'
  },
  nightlyPriceInclTaxes: {
    id: 'priceDisplay.nightly',
    defaultMessage: 'Nightly price incl tax & fees'
  }
})

export const CurrencySelector: React.FC<Props> = ({
  isMobile = false,
  currencies,
  onChange,
  value,
  onSearchInputFocus,
  showTotalPrices,
  showPriceDisplaySetting,
  onChangePriceDisplay,
  includeTaxes = false
}) => {
  const {formatMessage} = useIntl()

  const renderList = (items: CurrencyType[], isSearching = false) => (
    <>
      {showPriceDisplaySetting && !isSearching && (
        <PriceDisplayWrapper>
          <PriceDisplayTitle variant={isMobile ? 'titleM' : 'titleL'}>
            <FormattedMessageWrapper
              id="priceDisplay"
              defaultMessage="Price display"
            />
          </PriceDisplayTitle>
          <RadioGroup>
            <div id="PriceDisplay--nightly">
              <Radio
                hasTouchArea
                fullWidth
                checked={!showTotalPrices}
                name={`reviewScore-any`}
                label={formatMessage(
                  includeTaxes
                    ? messages.nightlyPriceInclTaxes
                    : messages.nightlyPrice
                )}
                onChange={() => onChangePriceDisplay?.(false)}
                labelSize="bodyM"
              />
            </div>
            <div id="PriceDisplay--total">
              <Radio
                hasTouchArea
                fullWidth
                checked={Boolean(showTotalPrices)}
                name={`reviewScore-any`}
                label={formatMessage({
                  id: 'priceDisplay.total',
                  defaultMessage: 'Total price incl tax & fees'
                })}
                onChange={() => onChangePriceDisplay?.(true)}
                labelSize="bodyM"
              />
            </div>
          </RadioGroup>
        </PriceDisplayWrapper>
      )}

      <SelectorList
        isMobile={isMobile}
        items={items}
        value={value}
        onChange={onChange}
        getValue={item => item.code}
        popularItemsTitle={formatMessage({
          id: 'popularCurrencies',
          defaultMessage: 'Popular currencies'
        })}
        otherItemsTitle={formatMessage({
          id: 'allCurrencies',
          defaultMessage: 'All currencies'
        })}
        splitPopularAndOther={splitPopularAndOther}
        renderItem={item => (
          <>
            <div>
              <Text
                variant={isMobile ? 'bodyM' : 'titleS'}
                colorPath="content.neutral.primary"
              >
                {item.displayName}
              </Text>
              <Text
                variant={isMobile ? 'bodyM' : 'bodyS'}
                colorPath={'content.neutral.secondary'}
                translate="no"
              >
                {item.code} {item.code !== item.symbol && <>- {item.symbol}</>}
              </Text>
            </div>
          </>
        )}
        isSearching={isSearching}
      />
    </>
  )
  if (isMobile) {
    return (
      <SearchInput
        items={currencies}
        itemMatcher={SearchInput.matchers.default([
          'displayName',
          'code',
          'symbol'
        ])}
        onFocus={onSearchInputFocus}
      >
        {renderList}
      </SearchInput>
    )
  }

  return renderList(currencies)
}
